import { ORPCError } from "@orpc/client";
import { t } from "i18next";
import { ZodError } from "zod";
import { prepareAudioVisitFile } from "@/lib/audio-visit-upload";
import { uploadOwnedAudioFile } from "@/lib/owned-audio-upload";
import { runWithStallTimeout } from "@/lib/upload-watchdog";
import { createAudioVisit } from "@/services/audio-uploads";
import {
  deleteRecording,
  getRecording,
  listRecordings,
  readRecordingAudio,
  updateRecording,
} from "./db";
import {
  canAccessOfflineRecording,
  hasPublicRecordingIdentity,
  isPublicVisitForm,
  offlineAccountKey,
  offlineIdentityUpgrade,
} from "./identity";
import {
  HEARTBEAT_STALE_MS,
  isExpired,
  OWNED_UPLOAD_MAX_BYTES,
  retryDelay,
} from "./limits";
import type { OfflineRecording, OfflineVisitForm } from "./types";

export interface QueueAccount {
  uid: string;
  accountPublicId: string;
}

export interface QueueEnvironment {
  /** The signed-in account; recordings of other accounts are left alone. */
  account: () => QueueAccount | null;
  online: () => boolean;
  now?: () => number;
  /** A recording was confirmed by the server and removed from the device. */
  onConfirmed?: (
    recording: OfflineRecording,
    visitPublicId: string | null,
  ) => void;
  /** Rows changed (status, progress, removal). */
  onChange?: () => void;
  onProgress?: (id: string, percent: number) => void;
}

const accountKey = offlineAccountKey;

// Stored on the recording in the interface language at the time of failure.
const legacyUncertain = (): string =>
  t("offlineRecording.errors.legacyUncertain");
const tooLarge = (): string => t("offlineRecording.errors.tooLarge");
const legacyRetired = (): string => t("offlineRecording.errors.legacyRetired");
const localAudioChanged = (): string =>
  t("offlineRecording.errors.localAudioChanged");
const noAudio = (): string => t("offlineRecording.errors.noAudio");

const isAbort = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError";

/** Errors that will not change by retrying the same request. */
const isFinal = (error: unknown): boolean =>
  error instanceof ZodError ||
  (error instanceof ORPCError &&
    ["BAD_REQUEST", "FORBIDDEN", "CONFLICT", "PAYMENT_REQUIRED"].includes(
      String(error.code),
    ));

/** Shown instead of validation details, which are not for the user. */
export const SETTINGS_REJECTED =
  "ROSC could not accept this recording's settings, so nothing was saved yet. The audio stays on this device: Retry, or download it and contact support.";

/** A stored error that is raw validation output (saved by older versions). */
export const isRawValidationError = (message: string): boolean =>
  /^\s*[[{]/.test(message);

const describe = (error: unknown): string => {
  if (
    error instanceof ZodError ||
    (error instanceof ORPCError && error.code === "BAD_REQUEST")
  ) {
    console.warn("Offline recording settings were rejected", error);
    return SETTINGS_REJECTED;
  }
  return error instanceof ORPCError
    ? error.message || t("offlineRecording.errors.rejected")
    : error instanceof Error && error.message
      ? error.message
      : t("offlineRecording.errors.unfinished");
};

type AudioSettings = Parameters<typeof prepareAudioVisitFile>[0]["settings"];
const settingsOf = (
  recording: OfflineRecording,
  form: OfflineVisitForm,
): AudioSettings => ({
  duration: Math.max(1, Math.ceil(recording.durationMs / 1000)),
  language: recording.capture.language,
  ...(form.title?.trim() ? { title: form.title.trim() } : {}),
  style: form.style,
  format: form.format,
  exam: form.examination,
  template: form.templateName,
  ...(form.customTemplatePublicId !== null
    ? { customTemplatePublicId: form.customTemplatePublicId }
    : {}),
  diarize: recording.capture.diarize,
  ...(recording.capture.providerId
    ? {
        // Validated by the contract schema when the upload is prepared.
        providerId: recording.capture.providerId as AudioSettings["providerId"],
      }
    : {}),
  ...(form.instructions !== undefined
    ? { instructions: form.instructions }
    : {}),
  ...(form.skipShortcuts?.length ? { skipShortcuts: form.skipShortcuts } : {}),
  ...(form.recordingConsent ? { recordingConsent: true as const } : {}),
});

/**
 * Puts a recording in the upload queue with its visit form. The path and the
 * idempotency keys are fixed the first time, so a recording is always sent
 * with the same keys (no duplicate visit, no second charge).
 */
export const enqueueRecording = async (
  id: string,
  form: OfflineVisitForm,
  { now = Date.now() }: { now?: number } = {},
): Promise<OfflineRecording> => {
  if (!isPublicVisitForm(form)) throw new Error(offlineIdentityUpgrade());
  const row = await updateRecording(id, (current) => {
    if (!hasPublicRecordingIdentity(current))
      throw new Error(offlineIdentityUpgrade());
    return {
      ...current,
      status: "queued",
      form: current.upload ? (current.form ?? form) : form,
      heartbeatAt: 0,
      updatedAt: now,
      upload: current.upload
        ? {
            ...current.upload,
            nextAttemptAt: now,
            blocked: false,
            lastError: null,
          }
        : {
            path: "owned",
            attempts: 0,
            nextAttemptAt: now,
            lastError: null,
            blocked: false,
            uploadKey: crypto.randomUUID(),
            visitKey: crypto.randomUUID(),
          },
    };
  });
  if (!row) throw new Error("This recording is no longer on this device.");
  return row;
};

/** Recordings nobody is recording or submitting (crash, reload, closed sheet). */
export const isRecoverable = (
  recording: OfflineRecording,
  now = Date.now(),
  held: Set<string> | null = null,
): boolean =>
  (!hasPublicRecordingIdentity(recording) ||
    recording.status === "recording" ||
    recording.status === "stopped") &&
  (recording.heartbeatAt === 0 ||
    (held
      ? !held.has(recording.id)
      : now - recording.heartbeatAt > HEARTBEAT_STALE_MS));

/** Removes recordings past the TTL, for every account on this device. */
export const purgeExpiredRecordings = async (
  now = Date.now(),
): Promise<OfflineRecording[]> => {
  const removed: OfflineRecording[] = [];
  for (const recording of await listRecordings()) {
    if (
      !hasPublicRecordingIdentity(recording) ||
      !isExpired(recording.createdAt, now)
    )
      continue;
    // Never pull audio from under a live recorder or upload.
    if (
      (recording.status === "recording" || recording.status === "uploading") &&
      now - recording.heartbeatAt <= HEARTBEAT_STALE_MS
    )
      continue;
    await deleteRecording(recording.id);
    removed.push(recording);
  }
  return removed;
};

const withUploadLock = async (work: () => Promise<void>): Promise<boolean> => {
  const locks = (
    globalThis.navigator as Navigator & {
      locks?: {
        request: (
          name: string,
          options: { ifAvailable: boolean },
          callback: (lock: unknown) => Promise<void>,
        ) => Promise<void>;
      };
    }
  )?.locks;
  if (!locks?.request) {
    await work();
    return true;
  }
  let ran = false;
  await locks.request(
    "rosc-offline-recording-upload",
    { ifAvailable: true },
    async (lock) => {
      if (!lock) return;
      ran = true;
      await work();
    },
  );
  return ran;
};

/**
 * The upload queue. One instance per page; uploads run one at a time and,
 * where Web Locks exist, in one tab at a time.
 */
export class OfflineUploadQueue {
  private readonly env: QueueEnvironment;
  private readonly now: () => number;
  private draining: Promise<void> | null = null;
  private again = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly running = new Map<string, AbortController>();
  private stopped = false;

  constructor(env: QueueEnvironment) {
    this.env = env;
    this.now = env.now ?? Date.now;
  }

  isRunning(id: string): boolean {
    return this.running.has(id);
  }

  /** Stops timers and aborts the running upload (sign-out, tests). */
  dispose(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    for (const controller of this.running.values()) controller.abort();
  }

  /** Back online: every waiting item is due now. */
  async wake(): Promise<void> {
    const account = this.env.account();
    if (!account) return;
    const now = this.now();
    for (const row of await listRecordings()) {
      if (
        row.account === accountKey(account) &&
        row.status === "failed" &&
        row.upload &&
        !row.upload.blocked
      )
        await updateRecording(row.id, (current) =>
          current.upload
            ? { ...current, upload: { ...current.upload, nextAttemptAt: now } }
            : null,
        );
    }
    await this.drain();
  }

  /** Manual Retry; retired legacy rows remain blocked with audio intact. */
  async retry(id: string): Promise<void> {
    const row = await getRecording(id);
    const account = this.env.account();
    if (!row || !account || !canAccessOfflineRecording(row, account))
      throw Error("Recording belongs to another account");
    if (!hasPublicRecordingIdentity(row)) throw Error(offlineIdentityUpgrade());
    await updateRecording(id, (current) =>
      current.upload &&
      (current.status === "failed" || current.status === "uncertain")
        ? {
            ...current,
            status: "queued",
            upload: {
              ...current.upload,
              nextAttemptAt: this.now(),
              blocked: false,
            },
          }
        : null,
    );
    this.env.onChange?.();
    await this.drain();
  }

  /** Cancels a running upload of this recording, then removes it. */
  async discard(id: string): Promise<void> {
    const row = await getRecording(id);
    const account = this.env.account();
    if (!row || !account || !canAccessOfflineRecording(row, account))
      throw Error("Recording belongs to another account");
    this.running.get(id)?.abort();
    await deleteRecording(id);
    this.env.onChange?.();
  }

  /** Runs every due item; concurrent calls share one pass. */
  drain(): Promise<void> {
    if (this.stopped) return Promise.resolve();
    if (this.draining) {
      this.again = true;
      return this.draining;
    }
    this.draining = (async () => {
      try {
        do {
          this.again = false;
          await withUploadLock(async () => {
            await this.resetAbandoned();
            for (;;) {
              if (this.stopped || !this.env.online()) return;
              const next = await this.nextDue();
              if (!next) return;
              await this.process(next);
            }
          });
        } while (this.again && !this.stopped);
      } finally {
        this.draining = null;
        await this.schedule();
      }
    })();
    return this.draining;
  }

  /** Rows left `uploading` by a closed tab go back to the queue. */
  private async resetAbandoned(): Promise<void> {
    const now = this.now();
    for (const row of await listRecordings()) {
      if (!hasPublicRecordingIdentity(row)) continue;
      if (row.status !== "uploading" || this.running.has(row.id)) continue;
      // Without Web Locks another tab might still be uploading it.
      if (
        !(globalThis.navigator as { locks?: unknown })?.locks &&
        now - row.heartbeatAt <= HEARTBEAT_STALE_MS * 4
      )
        continue;
      await updateRecording(row.id, (current) =>
        current.status === "uploading"
          ? {
              ...current,
              status:
                current.upload?.path === "legacy" &&
                current.upload.legacyPhase === "registering"
                  ? "uncertain"
                  : "failed",
              upload: current.upload
                ? {
                    ...current.upload,
                    nextAttemptAt: now,
                    lastError:
                      current.upload.path === "legacy" &&
                      current.upload.legacyPhase === "registering"
                        ? legacyUncertain()
                        : t("offlineRecording.errors.interrupted"),
                  }
                : undefined,
            }
          : null,
      );
    }
  }

  private async nextDue(): Promise<OfflineRecording | null> {
    const account = this.env.account();
    if (!account) return null;
    const now = this.now();
    const due = (await listRecordings())
      .filter(
        (row) =>
          hasPublicRecordingIdentity(row) &&
          row.account === accountKey(account) &&
          !!row.upload &&
          !!row.form &&
          (row.status === "queued" ||
            (row.status === "failed" &&
              !row.upload.blocked &&
              row.upload.nextAttemptAt <= now)),
      )
      .sort((a, b) => a.createdAt - b.createdAt);
    return due[0] ?? null;
  }

  private async schedule(): Promise<void> {
    if (this.stopped) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const account = this.env.account();
    if (!account) return;
    let next = Number.POSITIVE_INFINITY;
    try {
      for (const row of await listRecordings())
        if (
          hasPublicRecordingIdentity(row) &&
          row.account === accountKey(account) &&
          row.status === "failed" &&
          row.upload &&
          !row.upload.blocked
        )
          next = Math.min(next, row.upload.nextAttemptAt);
    } catch {
      return;
    }
    if (!Number.isFinite(next)) return;
    this.timer = setTimeout(
      () => {
        this.timer = null;
        void this.drain();
      },
      Math.max(1_000, next - this.now()),
    );
  }

  private async process(row: OfflineRecording): Promise<void> {
    const account = this.env.account();
    const upload = row.upload;
    const form = row.form;
    if (
      !account ||
      !upload ||
      !form ||
      !hasPublicRecordingIdentity(row) ||
      row.account !== accountKey(account)
    )
      return;
    const isCurrent = (): boolean => this.env.account() === account;
    const controller = new AbortController();
    this.running.set(row.id, controller);
    const started = await updateRecording(row.id, (current) => ({
      ...current,
      status: "uploading",
      heartbeatAt: this.now(),
      upload: current.upload
        ? {
            ...current.upload,
            attempts: current.upload.attempts + 1,
            lastError: null,
          }
        : undefined,
    }));
    this.env.onChange?.();
    if (!started?.upload) {
      this.running.delete(row.id);
      return;
    }
    try {
      if (upload.path === "legacy")
        throw new BlockedUploadError(legacyRetired());
      const visitPublicId = await this.sendOwned(
        started,
        form,
        account,
        controller.signal,
      );
      if (controller.signal.aborted || !isCurrent()) return;
      await deleteRecording(row.id);
      if (!isCurrent()) return;
      this.env.onConfirmed?.(started, visitPublicId);
    } catch (error) {
      if (!isCurrent()) return;
      if (controller.signal.aborted && isAbort(error)) return;
      await this.recordFailure(row.id, error);
    } finally {
      this.running.delete(row.id);
      this.env.onChange?.();
    }
  }

  private async recordFailure(id: string, error: unknown): Promise<void> {
    const now = this.now();
    await updateRecording(id, (current) => {
      if (!current.upload) return null;
      const uncertain =
        current.upload.path === "legacy" &&
        current.upload.legacyPhase === "registering";
      const blocked =
        error instanceof BlockedUploadError || (!uncertain && isFinal(error));
      return {
        ...current,
        status: uncertain ? "uncertain" : "failed",
        upload: {
          ...current.upload,
          blocked,
          nextAttemptAt: now + retryDelay(current.upload.attempts),
          lastError:
            error instanceof BlockedUploadError
              ? error.message
              : uncertain
                ? legacyUncertain()
                : describe(error),
        },
      };
    }).catch(() => undefined);
  }

  private touch(id: string): void {
    void updateRecording(id, (current) => ({
      ...current,
      heartbeatAt: this.now(),
    })).catch(() => undefined);
  }

  private async sendOwned(
    row: OfflineRecording,
    form: OfflineVisitForm,
    account: QueueAccount,
    signal: AbortSignal,
  ): Promise<string> {
    const upload = row.upload;
    const requireCurrent = (): void => {
      if (signal.aborted || this.env.account() !== account)
        throw new DOMException("Account changed", "AbortError");
    };
    requireCurrent();
    if (!upload) throw new BlockedUploadError(noAudio());
    const audio = await readRecordingAudio(row.id, row.mimeType);
    if (audio.size === 0) throw new BlockedUploadError(noAudio());
    if (audio.size > OWNED_UPLOAD_MAX_BYTES)
      throw new BlockedUploadError(tooLarge());
    const prepared = await prepareAudioVisitFile({
      file: audio,
      settings: settingsOf(row, form),
      signal,
    });
    if (
      upload.checksumSha256 &&
      upload.checksumSha256 !== prepared.input.checksumSha256
    )
      throw new BlockedUploadError(localAudioChanged());
    if (!upload.checksumSha256)
      await updateRecording(row.id, (current) =>
        current.upload
          ? {
              ...current,
              upload: {
                ...current.upload,
                checksumSha256: prepared.input.checksumSha256,
              },
            }
          : null,
      );
    requireCurrent();
    let uploadId = upload.uploadId;
    const isCurrent = (): boolean =>
      !signal.aborted && this.env.account() === account;
    return runWithStallTimeout(
      async ({ signal: attempt, touch }) => {
        uploadId = await uploadOwnedAudioFile({
          file: prepared.file,
          input: prepared.input,
          identity: {
            key: upload.uploadKey,
            userUid: account.uid,
            signal: attempt,
          },
          uploadId,
          onCreated: (id) => {
            if (!isCurrent()) return;
            uploadId = id;
            touch();
            void updateRecording(row.id, (current) =>
              current.upload
                ? { ...current, upload: { ...current.upload, uploadId: id } }
                : null,
            ).catch(() => undefined);
          },
          isCurrent,
          onProgress: ({ loaded, total }) => {
            if (!isCurrent()) return;
            touch();
            this.touch(row.id);
            this.env.onProgress?.(row.id, Math.floor((loaded / total) * 100));
          },
        });
        requireCurrent();
        touch();
        await updateRecording(row.id, (current) =>
          current.upload
            ? { ...current, upload: { ...current.upload, uploadId } }
            : null,
        );
        requireCurrent();
        const result = await createAudioVisit(
          { ...prepared.body, audioUploadId: uploadId },
          { key: upload.visitKey, userUid: account.uid, signal: attempt },
        );
        return result.public_id;
      },
      { signal },
    );
  }
}

/** A failure that needs the user (too large, rejected, missing audio). */
export class BlockedUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BlockedUploadError";
  }
}

/** Reads one row (for the sheet after enqueueing). */
export const readQueuedRecording = getRecording;
